import "./driveSetup.css";
import type { EndlessSession } from "../endless/session";
import type { DriveOptions } from "../endless/engine";
import type {
  Control,
  DriverData,
  NeuroDriveData,
  ParticipantSelection,
  QuickRaceSelection,
  RaceResult,
  VehicleData,
  VehicleId,
} from "../types";
import { QuickRaceEngine } from "../core/race";
import { RaceAudioController } from "../audio/audioController";
import { RaceCanvasRenderer, drawTrackPreview, drawVehiclePreview } from "../render/canvasRenderer";
import {
  countdownCue,
  exactRematchSelection,
  finishCue,
  raceHasStarted,
} from "./racePresentation";
import driverPortraitsUrl from "../../../assets/images/driver-portraits-v1.png";
import humanDriverHelmetUrl from "../../../assets/images/human-driver-helmet-v1.png";
import welcomeHeroUrl from "../../../assets/images/welcome-hero-v1.png";

import { readViewPreference, saveViewPreference } from "./viewPreference";
import type { CockpitRenderer } from "../render/cockpit/renderer";

type Route = "home" | "play" | "ai-lab" | "drive";
type Picker = { kind: "driver" | "vehicle"; slotId: string } | null;

const routePaths: Record<Route, string> = {
  home: "/",
  play: "/play",
  drive: "/drive",
  "ai-lab": "/ai-lab",
};

function routeFromPath(path: string): Route {
  if (path.startsWith("/drive")) return "drive";
  if (path.startsWith("/play")) return "play";
  if (path.startsWith("/ai-lab")) return "ai-lab";
  if (path.startsWith("/about")) return "ai-lab";
  return "home";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  })[character] ?? character);
}

function formatTime(value: number | null): string {
  if (value === null) return "—";
  const minutes = Math.floor(value / 60);
  const seconds = value - minutes * 60;
  return minutes > 0 ? `${minutes}:${seconds.toFixed(2).padStart(5, "0")}` : `${seconds.toFixed(2)} s`;
}

function colorCss(color: [number, number, number]): string {
  return `rgb(${color[0]} ${color[1]} ${color[2]})`;
}

export class NeuroDriveApp {
  private readonly root: HTMLDivElement;
  private data!: NeuroDriveData;
  private route: Route = routeFromPath(window.location.pathname);
  private selection!: QuickRaceSelection;
  private picker: Picker = null;
  private endless: EndlessSession | null = null;
  private driveRequest = 0;
  private driveOptions: DriveOptions = { seed: 4721, vehicle: "standard", startHour: 16, startBiome: 0 };
  private engine: QuickRaceEngine | null = null;
  private renderer: RaceCanvasRenderer | null = null;
  private cockpit: CockpitRenderer | null = null;
  private viewAnimation: Animation | null = null;
  private raceView: "2d" | "3d" = "2d";
  private preferredView = readViewPreference();
  private viewRequest = 0;
  private loading3d = false;
  private animationFrame: number | null = null;
  private previousFrame = 0;
  private accumulator = 0;
  private paused = false;
  private raceFinishedAt: number | null = null;
  private countdownStartedAt = 0;
  private raceStarted = false;
  private winnerAnnouncedAt: number | null = null;
  private announcerKey = "";
  private focusedParticipantId: string | null = null;
  private leavingRace = false;
  private keys = new Set<string>();
  private readonly audio = new RaceAudioController();

  constructor(root: HTMLDivElement) {
    this.root = root;
    this.audio.setStateListener(() => this.updateAudioButtons());
    const activateAudio = (event: Event): void => {
      if ((event.target as Element | null)?.closest?.("[data-audio-toggle]")) return;
      void this.audio.activate();
    };
    window.addEventListener("pointerdown", activateAudio, { capture: true });
    window.addEventListener("keydown", activateAudio, { capture: true });
  }

  async start(): Promise<void> {
    const response = await fetch("/data/neurodrive-web-v6.json", { cache: "no-cache" });
    if (!response.ok) throw new Error(`Release data svarede HTTP ${response.status}.`);
    this.data = await response.json() as NeuroDriveData;
    if (this.data.schema_version !== 6) throw new Error("Webdata har en ukendt version.");
    await this.audio.prepare();
    this.selection = this.defaultSelection();
    window.addEventListener("popstate", () => {
      this.stopRace();
      this.route = routeFromPath(window.location.pathname);
      this.render();
    });
    window.addEventListener("keydown", (event) => this.handleKey(event, true), { passive: false });
    window.addEventListener("keyup", (event) => this.handleKey(event, false), { passive: false });
    this.render();
    this.audio.setScene("menu");
    // Forsøg straks. Browsere, der tillader autoplay, starter her; de øvrige
    // genbruger samme nummer ved den første almindelige klik-/tastehandling.
    void this.audio.activate();
  }

  private defaultSelection(): QuickRaceSelection {
    const showcase = this.data.showcase;
    return {
      trackKey: showcase.track,
      laps: showcase.laps,
      mode: showcase.race_mode,
      rabbitCount: 0,
      raceSeed: showcase.race_seed,
      participants: showcase.participants.map((participant, index) => ({
        slotId: `slot-${index + 1}`,
        driverId: index === 0 ? "human" : participant.driver_id,
        vehicleId: index === 0 ? "standard" : participant.vehicle_id,
        color: this.data.race.colors[index] ?? [230, 230, 230],
      })),
    };
  }

  private navigate(route: Route): void {
    this.stopRace();
    this.route = route;
    window.history.pushState({}, "", routePaths[route]);
    this.render();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  private render(): void {
    this.root.innerHTML = `${this.header()}<main id="main">${this.page()}</main>${this.footer()}${this.pickerMarkup()}`;
    this.bindCommonActions();
    if (this.route === "play") this.bindSetupActions();
    if (this.route === "drive") this.bindDriveSetup();
    this.bindPickerActions();
    requestAnimationFrame(() => this.drawPreviews());
  }

  private header(): string {
    const nav = (["home", "play", "drive", "ai-lab"] as Route[]).map((route) => {
      const label = route === "home" ? "Forside" : route === "play" ? "Kør løb" : route === "drive" ? "Endeløs" : "Sådan virker det";
      return `<button class="nav-link ${this.route === route ? "is-active" : ""}" type="button" data-route="${route}">${label}</button>`;
    }).join("");
    return `
      <header class="site-header">
        <button class="brand" type="button" data-route="home" aria-label="NeuroDrive forside">
          <img class="brand-mark brand-icon" src="/icon-192.png" alt=""><span><strong>NEURODRIVE</strong><small>AI PÅ FIRE HJUL</small></span>
        </button>
        <nav aria-label="Hovedmenu">${nav}</nav>
        <div class="header-actions">${this.audioButtonMarkup()}<button class="button button-small button-accent" type="button" data-route="play">Kør et løb <span aria-hidden="true">→</span></button></div>
      </header>`;
  }

  private audioButtonMarkup(): string {
    const state = this.audio.muted ? "muted" : this.audio.awaitingActivation ? "waiting" : "playing";
    const label = state === "muted" ? "Slå lyd til" : state === "waiting" ? "Aktivér musik" : "Slå lyd fra";
    const text = state === "muted" ? "Lyd fra" : state === "waiting" ? "Lyd venter" : "Lyd";
    return `<button class="audio-toggle" type="button" data-audio-toggle data-audio-state="${state}" aria-pressed="${this.audio.muted}" aria-label="${label}"><span aria-hidden="true">${this.audio.muted ? "×" : "♪"}</span>${text}</button>`;
  }

  private footer(): string {
    return `
      <footer class="site-footer">
        <span>NeuroDrive · fysik ${escapeHtml(this.data.game_semantics.reference_commit)}</span>
        <span>Version ${escapeHtml(__BUILD_COMMIT__)}${__BUILD_DIRTY__ ? " · lokale ændringer" : ""}</span>
        <span>Kører lokalt i browseren · ingen sporing</span>
      </footer>`;
  }

  private page(): string {
    if (this.route === "drive") return this.drivePage();
    if (this.route === "play") return this.setupPage();
    if (this.route === "ai-lab") return this.aiLabPage();
    return this.homePage();
  }

  private drivePage(): string {
    return `<section class="drive-setup"><div class="drive-hero"><small>NEURODRIVE / FOREVER</small><h1>Vejen slutter aldrig.</h1><p>Tag din bil ud under åben himmel. Skoven bliver til kyst, højland og små landsbyer. Dagen går på hæld, lygterne tændes, og vejen fortsætter.</p></div><div class="drive-settings"><h2>Din bil</h2><div class="drive-vehicles">${this.data.vehicles.map(v => `<button class="drive-vehicle" data-drive-vehicle="${v.key}" aria-pressed="${this.driveOptions.vehicle === v.key}"><canvas data-vehicle-preview="${v.key}" data-color="25,208,213" aria-hidden="true"></canvas><strong>${escapeHtml(v.name)}</strong></button>`).join("")}</div><div class="drive-fields"><label>Start din tur ved<select data-drive-biome-select>${["Skovlandet", "Kystvejen", "Højlandet", "Landsbyerne"].map((b,i)=>`<option value="${i}" ${this.driveOptions.startBiome === i ? "selected" : ""}>${b}</option>`).join("")}</select></label><label>Tid på dagen<select data-drive-hour>${[[8,"Morgen · 08:00"],[12,"Middag · 12:00"],[16,"Eftermiddag · 16:00"],[18,"Solnedgang · 18:00"],[22,"Nat · 22:00"]].map(([h,label])=>`<option value="${h}" ${this.driveOptions.startHour === h ? "selected" : ""}>${label}</option>`).join("")}</select></label></div><button class="button button-accent button-large" data-start-drive>Kør ud i det blå →</button> <button class="button button-quiet button-large" data-route="play">Kør et almindeligt løb</button><p role="status" data-drive-message></p><p class="drive-rules">Hold til højre, giv plads til overhalinger, og pas på modkørende. Kun et hårdt frontalt sammenstød med en anden bil afslutter turen. Kaniner kan få bilen til at glide i to sekunder. Et minut på vejen svarer til to timer på uret.</p><p class="drive-rules">WASD eller piletaster styrer bilen. Mellemrum pauser. På touchskærme får du køreknapper. Denne køretur bruger 3D.</p></div></section>`;
  }

  private bindDriveSetup(): void {
    this.root.querySelectorAll<HTMLElement>("[data-drive-vehicle]").forEach(b => b.addEventListener("click", () => { this.driveOptions.vehicle = b.dataset.driveVehicle as VehicleId; this.render(); }));
    this.root.querySelector<HTMLSelectElement>("[data-drive-biome-select]")?.addEventListener("change", e => { this.driveOptions.startBiome = Number((e.target as HTMLSelectElement).value); });
    this.root.querySelector<HTMLSelectElement>("[data-drive-hour]")?.addEventListener("change", e => { this.driveOptions.startHour = Number((e.target as HTMLSelectElement).value); });
    this.root.querySelector("[data-start-drive]")?.addEventListener("click", () => { void this.startDrive(); });
  }

  private async startDrive(): Promise<void> {
    this.endless?.dispose(); this.endless = null;
    const request = ++this.driveRequest;
    const button = this.root.querySelector<HTMLButtonElement>("[data-start-drive]");
    if (button) { button.disabled = true; button.textContent = "Åbner din køretur…"; }
    try {
      const { EndlessSession } = await import("../endless/session");
      if (request !== this.driveRequest || this.route !== "drive") return;
      this.endless = new EndlessSession(this.root.querySelector<HTMLElement>("#main")!, this.data.vehicles, { ...this.driveOptions }, {
        music: scene => { if (scene === "forever") this.audio.beginDrive(); else this.audio.setScene("menu"); },
        countdown: cue => this.audio.countdown(cue),
        update: (...args) => this.audio.updateDrive(...args),
        silence: () => this.audio.silenceRace(),
        toggle: () => this.audio.toggleMuted(),
        muted: () => this.audio.muted,
      }, () => this.navigate("drive"), () => { void this.startDrive(); });
    } catch {
      if (request !== this.driveRequest) return;
      this.endless?.dispose(); this.endless = null; this.audio.silenceRace(); this.audio.setScene("menu"); this.render();
      this.root.querySelector("[data-drive-message]")!.textContent = "3D kunne ikke starte. Prøv igen, eller vælg Kør løb for at køre i 2D.";
    }
  }

  private homePage(): string {
    return `
      <section class="hero home-hero">
        <img class="welcome-hero-image" src="${welcomeHeroUrl}" alt="Illustreret miniature-racerbane med bro, vand, flyvehop og farverige biler">
        <div class="hero-copy">
          <p class="eyebrow"><span></span> EN LILLE AI LÆRER AT KØRE</p>
          <h1>Vi lod små AI'er lære at køre bil.</h1>
          <p class="hero-lede">De får 21 tal om fart og det, der er tæt på. Så vælger de rat og speeder. De bedste bliver kopieret, ændret lidt og prøver igen.</p>
          <div class="hero-actions">
            <button class="button button-accent button-large" type="button" data-route="play">Kør mod dem <span aria-hidden="true">↗</span></button>
            <button class="button button-quiet button-large" type="button" data-route="drive">Endeløs køretur ∞</button>
            <button class="button button-quiet button-large" type="button" data-route="ai-lab">Sådan lærer de <span aria-hidden="true">→</span></button>
          </div>
          <div class="proof-strip" aria-label="NeuroDrive i tal">
            <div><strong>21</strong><span>tal ind</span></div>
            <div><strong>8</strong><span>neuroner</span></div>
            <div><strong>2</strong><span>valg ud</span></div>
          </div>
        </div>
      </section>`;
  }

  private setupPage(): string {
    return `
      <section class="setup-layout setup-layout-direct section-shell">
        <div class="setup-main">
          <div class="section-heading"><div><p class="step-label">01 · BANE</p><h2>Vælg bane</h2></div><p>${escapeHtml(this.track().description)}</p></div>
          <div class="track-picker" role="radiogroup" aria-label="Bane">
            ${this.data.tracks.filter((track) => this.data.race.selectable_track_keys.includes(track.key)).map((track) => `
              <button type="button" class="track-option ${track.key === this.selection.trackKey ? "is-selected" : ""}" data-track="${track.key}" role="radio" aria-checked="${track.key === this.selection.trackKey}">
                <canvas data-track-preview="${track.key}" aria-hidden="true"></canvas>
                <span><strong>${escapeHtml(track.name)}</strong></span>
                <i aria-hidden="true"></i>
              </button>`).join("")}
          </div>
          <div class="section-heading drivers-heading"><div><p class="step-label">02 · KØRERE</p><h2>Vælg kørere og biler</h2></div><div class="grid-actions"><button class="button button-quiet" type="button" data-randomize-grid>Bland</button><button class="button button-quiet" type="button" data-clear-grid>Ryd</button></div></div>
          <div class="participant-grid">
            ${this.selection.participants.map((participant, index) => this.participantCard(participant, index)).join("")}
            ${this.selection.participants.length < this.data.race.maximum_participants ? `<button class="add-driver-card" type="button" data-add-driver><span>+</span><strong>Tilføj kører</strong><small>Højst ${this.data.race.maximum_participants}</small></button>` : ""}
          </div>
        </div>
        <aside class="race-config-card">
          <p class="step-label">03 · REGLER</p>
          <h2>Klar?</h2>
          <label class="field-label" for="laps">Omgange</label>
          <select id="laps" data-laps>${[1, 3, 5].map((laps) => `<option value="${laps}" ${laps === this.selection.laps ? "selected" : ""}>${laps} omgang${laps === 1 ? "" : "e"}</option>`).join("")}</select>
          <span class="field-label">Sammenstød</span>
          <div class="segment-control" role="group" aria-label="Sammenstød">
            <button type="button" data-mode="off" class="${this.selection.mode === "off" ? "is-selected" : ""}"><b>Fra</b><small>Kør igennem</small></button>
            <button type="button" data-mode="on" class="${this.selection.mode === "on" ? "is-selected" : ""}"><b>Til</b><small>Biler støder sammen</small></button>
          </div>
          <span class="field-label">Kaniner</span>
          <button class="rabbit-meadow" type="button" data-rabbit-cycle aria-label="${this.selection.rabbitCount} kaniner. Tryk for at ændre antal">
            <span class="rabbit-count"><strong>${this.selection.rabbitCount}</strong> kaniner</span>
            <span class="rabbit-grass" aria-hidden="true">${Array.from({ length: this.selection.rabbitCount }, (_, index) => `<i style="--rabbit-delay:${index * 0.08}s">🐇</i>`).join("")}</span>
          </button>
          <button class="button button-accent button-race" type="button" data-start-race ${this.selection.participants.length === 0 ? "disabled" : ""}>Start løbet <span aria-hidden="true">↗</span></button>
        </aside>
      </section>`;
  }

  private participantCard(participant: ParticipantSelection, index: number): string {
    const driver = participant.driverId === "human" ? null : this.driver(participant.driverId);
    const vehicle = this.vehicle(participant.vehicleId);
    const name = driver?.name ?? "Dig";
    const subtitle = driver?.subtitle ?? "Du styrer bilen";
    const portrait = driver
      ? `<span class="driver-avatar has-portrait" aria-label="${escapeHtml(driver.name)}" style="background-image:url('${driverPortraitsUrl}');--portrait-x:${(driver.portrait_index % 4) * 100 / 3}%;--portrait-y:${Math.floor(driver.portrait_index / 4) * 100 / 3}%"></span>`
      : `<span class="driver-avatar has-human-helmet" aria-label="Dig" style="background-image:url('${humanDriverHelmetUrl}')"></span>`;
    return `
      <article class="participant-card" style="--race-color:${colorCss(participant.color)}" data-slot="${participant.slotId}">
        <div class="participant-index">P${index + 1}</div>
        <button type="button" class="driver-identity participant-choice" data-change-driver="${participant.slotId}" aria-label="Skift kører: ${escapeHtml(name)}">${portrait}<span><strong class="driver-name">${escapeHtml(name)}</strong><p>${escapeHtml(subtitle)}</p></span></button>
        <button type="button" class="vehicle-choice participant-choice" data-change-vehicle="${participant.slotId}" aria-label="Skift bil: ${escapeHtml(vehicle.name)}">
          <canvas class="vehicle-preview" data-vehicle-preview="${vehicle.key}" data-color="${participant.color.join(",")}" aria-hidden="true"></canvas>
          <span class="participant-vehicle"><span>BIL</span><strong>${escapeHtml(vehicle.name)}</strong><small>${escapeHtml(vehicle.description)}</small></span>
        </button>
        <button type="button" class="remove-button" data-remove="${participant.slotId}" aria-label="Fjern ${escapeHtml(name)}">×</button>
      </article>`;
  }

  private pickerMarkup(): string {
    if (!this.picker) return "";
    const participant = this.selection.participants.find((item) => item.slotId === this.picker?.slotId);
    if (!participant) return "";
    if (this.picker.kind === "driver") {
      const hasHuman = this.selection.participants.some((item) => item.driverId === "human" && item.slotId !== participant.slotId);
      const grouped = (["full-race", "personality"] as const).map((category) => {
        const drivers = this.data.drivers.filter((driver) => driver.default_picker && driver.category === category);
        if (drivers.length === 0) return "";
        const label = category === "full-race" ? "Alsidige kørere" : "Dokumenterede personligheder";
        return `<div class="picker-group"><h3>${label}</h3><div class="driver-picker-grid">${drivers.map((driver) => this.driverPickerOption(driver, participant.driverId)).join("")}</div></div>`;
      }).join("");
      return `<div class="modal-backdrop" data-close-picker><section class="picker-modal" role="dialog" aria-modal="true" aria-labelledby="picker-title" tabindex="-1"><header><div><p class="eyebrow">VÆLG KØRER</p><h2 id="picker-title">Hvem skal sidde bag rattet?</h2></div><button type="button" data-close-picker aria-label="Luk kørervælger">×</button></header>${!hasHuman ? `<button class="human-picker-option ${participant.driverId === "human" ? "is-selected" : ""}" type="button" data-pick-driver="human"><span class="driver-avatar has-human-helmet" aria-hidden="true" style="background-image:url('${humanDriverHelmetUrl}')"></span><div><strong>Dig</strong><small>Tastatur eller trykknapper</small></div></button>` : ""}${grouped}</section></div>`;
    }
    return `<div class="modal-backdrop" data-close-picker><section class="picker-modal vehicle-modal" role="dialog" aria-modal="true" aria-labelledby="picker-title" tabindex="-1"><header><div><p class="eyebrow">VÆLG BIL</p><h2 id="picker-title">Samme kører. Ny bil.</h2></div><button type="button" data-close-picker aria-label="Luk bilvælger">×</button></header><div class="vehicle-picker-grid">${this.data.vehicles.map((vehicle) => this.vehiclePickerOption(vehicle, participant)).join("")}</div></section></div>`;
  }

  private driverPickerOption(driver: DriverData, selected: string): string {
    const x = (driver.portrait_index % 4) * 100 / 3;
    const y = Math.floor(driver.portrait_index / 4) * 100 / 3;
    return `<button type="button" class="driver-picker-option ${driver.id === selected ? "is-selected" : ""}" data-pick-driver="${driver.id}"><span class="driver-avatar has-portrait" style="background-image:url('${driverPortraitsUrl}');--portrait-x:${x}%;--portrait-y:${y}%"></span><div><strong>${escapeHtml(driver.name)}</strong><small>${escapeHtml(driver.subtitle)}</small><p>${escapeHtml(driver.description)}</p></div></button>`;
  }

  private vehiclePickerOption(vehicle: VehicleData, participant: ParticipantSelection): string {
    const labels = { speed: "fart", acceleration: "optræk", handling: "styring", mass: "vægt" };
    const stats = (["speed", "acceleration", "handling", "mass"] as const).map((stat) => `<span><small>${labels[stat]}</small><i><b style="width:${Math.min(100, vehicle.stats[stat] * 100)}%"></b></i></span>`).join("");
    return `<button type="button" class="vehicle-picker-option ${vehicle.key === participant.vehicleId ? "is-selected" : ""}" data-pick-vehicle="${vehicle.key}"><canvas data-vehicle-preview="${vehicle.key}" data-color="${participant.color.join(",")}"></canvas><div><h3>${escapeHtml(vehicle.name)}</h3><p>${escapeHtml(vehicle.description)}</p><div class="vehicle-stats">${stats}</div></div></button>`;
  }

  private aiLabPage(): string {
    const objective = this.data.ai_lab.objectives;
    const curriculum = this.data.ai_lab.curriculum;
    const stunts = this.data.ai_lab.stunts;
    const essaySections = this.data.ai_lab.explainer.sections.map((section) => `
      <details class="ai-essay-card" open>
        <summary><span>${escapeHtml(section.number)}</span><strong role="heading" aria-level="2">${escapeHtml(section.title)}</strong><small>${escapeHtml(section.summary)}</small></summary>
        <div class="ai-essay-copy">${section.paragraphs.map((paragraph) => `
          <article><h3>${escapeHtml(paragraph.title)}</h3><p>${escapeHtml(paragraph.body)}</p></article>`).join("")}</div>
      </details>`).join("");
    const trainingSteps = this.data.ai_lab.training_journey.steps.map((step, index) => `
      <article class="${index === 0 ? "is-first" : ""} ${step.role === "VALIDATION" ? "is-validation" : ""}">
        <span>${escapeHtml(step.number)}</span><h3>${escapeHtml(step.title)}</h3>
        <p>${escapeHtml(step.description)}</p><small>${step.role === "VALIDATION" ? "VALIDATION" : "TRÆNING"}</small>
      </article>`).join("");
    return `
      <section class="lab-hero lab-hero-simple section-shell"><p class="eyebrow"><span></span> SÅDAN VIRKER DET</p><h1>AI'en lærer ved at prøve.</h1><div class="lab-intro-copy"><p>NeuroDrive er et lille, synligt eksperiment i Python: bilfysik, baner og sensorer møder mange meget små neurale netværk. Her er hele vejen fra 21 tal til en frossen kører.</p></div></section>
      <section class="ai-essay section-shell" aria-label="Sådan træner NeuroDrive AI-kørere">${essaySections}</section>
      <section class="ai-loop-section section-shell">
        <div><p class="step-label">ÉN KØRER</p><h2>Fire trin. Igen og igen.</h2><p>Køreren får tal — ikke et billede eller et kort.</p></div>
        <div class="ai-loop" aria-label="Fra sensorer til kørsel">
          <div><span>01</span><strong>Se</strong><small>21 tal om fart og nærhed</small></div><i>→</i>
          <div><span>02</span><strong>Vælg</strong><small>rat + speeder/bremse</small></div><i>→</i>
          <div><span>03</span><strong>Lær</strong><small>de bedste fortsætter</small></div><i>→</i>
          <div><span>04</span><strong>Test</strong><small>på noget nyt</small></div>
          <p>kopiér · ændr lidt · prøv igen ↺</p>
        </div>
      </section>
      <section class="training-journey section-shell">
        <div class="short-heading"><p class="eyebrow">DET AUTOMATISKE FORLØB</p><h2>Systemet lægger træningsplanen.</h2><p>Desktopudgaven træner. Webudgaven bruger bagefter de samme frosne modeller, baner og regler.</p></div>
        <div class="training-journey-grid" aria-label="Det automatiske træningsforløb">
          ${trainingSteps}
        </div>
      </section>
      <section class="ai-boundaries section-shell">
        <article><p class="step-label">DET ER PROJEKTET</p><h2>Et synligt AI-forsøg</h2><p>Python træner kørerne. Browseren kører deres frosne netværk med de samme baner, biler og regler.</p></article>
        <article class="boundary-no"><p class="step-label">DET ER IKKE</p><h2>En tænkende chauffør</h2><p>AI'en har intet kort, ingen skjult autopilot, ingen følelser og ingen generativ model. Den har kun lært bestemte færdigheder i bestemte tests.</p></article>
      </section>
      <section class="lab-results section-shell">
        <div class="short-heading"><p class="eyebrow">DET HAR VI SET</p><h2>Tre korte resultater.</h2></div>
        <div class="result-card-grid">
          <article><span>KANINER</span><strong>${objective.balanced.incidents}/${objective.balanced.encounters} → ${objective.empathic.incidents}/${objective.empathic.encounters}</strong><h3>Et andet træningsmål gav færre påkørsler.</h3></article>
          <article><span>HUKOMMELSE</span><strong>${curriculum.continual.sequential_incidents.join(" / ")} → ${curriculum.continual.rehearsal_incidents.join(" / ")}</strong><h3>Repetition hjalp kørerne med at huske.</h3></article>
          <article><span>STUNTS</span><strong>${stunts.finishes}/${stunts.case_count}</strong><h3>Så mange forsøg nåede gennem stuntbanerne.</h3></article>
        </div>
      </section>
      <section class="public-roadmap section-shell">
        <div class="short-heading"><p class="eyebrow">DET NÆSTE</p><h2>Hvor er vi på vej hen?</h2></div>
        <div class="roadmap-line">
          <article><span>FÆRDIG</span><h3>Samme verden</h3><p>Web og desktop deler de otte oprindelige baner, fysikken og de frosne kørere. Web har desuden to ekstra ruter.</p></article>
          <article class="is-current"><span>NU</span><h3>Tre releasekørere</h3><p>Alma, Rita og Vanvidsbilisten bestod den samme urørte slutprøve og er med i spillet.</p></article>
          <article><span>SENERE</span><h3>Nye personligheder</h3><p>Nye kørere kommer kun med, hvis deres særpræg og grundkørsel begge holder i en ny låst prøve.</p></article>
        </div>
      </section>
      <section class="lab-cta section-shell"><div><h2>Prøv kørerne selv.</h2></div><div class="lab-cta-actions"><button class="button button-accent button-large" type="button" data-route="play">Kør et løb →</button></div></section>`;
  }

  private bindCommonActions(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-audio-toggle]").forEach((button) => button.addEventListener("click", () => {
      this.handleAudioButton();
    }));
    this.root.querySelectorAll<HTMLElement>("[data-route]").forEach((element) => {
      element.addEventListener("click", () => this.navigate(element.dataset.route as Route));
    });
  }

  private updateAudioButtons(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-audio-toggle]").forEach((button) => {
      const state = this.audio.muted ? "muted" : this.audio.awaitingActivation ? "waiting" : "playing";
      button.dataset.audioState = state;
      button.setAttribute("aria-pressed", String(this.audio.muted));
      button.setAttribute("aria-label", state === "muted" ? "Slå lyd til" : state === "waiting" ? "Aktivér musik" : "Slå lyd fra");
      button.innerHTML = `<span aria-hidden="true">${this.audio.muted ? "×" : "♪"}</span>${state === "muted" ? "Lyd fra" : state === "waiting" ? "Lyd venter" : "Lyd"}`;
    });
  }

  private handleAudioButton(): void {
    if (this.audio.awaitingActivation) void this.audio.activate();
    else this.audio.toggleMuted();
    this.updateAudioButtons();
  }

  private bindSetupActions(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-track]").forEach((button) => button.addEventListener("click", () => {
      this.selection.trackKey = button.dataset.track ?? this.data.race.selectable_track_keys[0]!;
      this.render();
    }));
    this.root.querySelector<HTMLSelectElement>("[data-laps]")?.addEventListener("change", (event) => {
      this.selection.laps = Number((event.target as HTMLSelectElement).value);
      this.render();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((button) => button.addEventListener("click", () => {
      this.selection.mode = button.dataset.mode === "on" ? "on" : "off";
      this.render();
    }));
    this.root.querySelector<HTMLButtonElement>("[data-rabbit-cycle]")?.addEventListener("click", () => {
      const options = this.data.race.rabbit_options;
      const current = Math.max(0, options.indexOf(this.selection.rabbitCount));
      this.selection.rabbitCount = options[(current + 1) % options.length] ?? 0;
      this.render();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-change-driver]").forEach((button) => button.addEventListener("click", () => {
      this.picker = { kind: "driver", slotId: button.dataset.changeDriver ?? "" };
      this.render();
    }));
    this.root.querySelectorAll<HTMLButtonElement>("[data-change-vehicle]").forEach((button) => button.addEventListener("click", () => {
      this.picker = { kind: "vehicle", slotId: button.dataset.changeVehicle ?? "" };
      this.render();
    }));
    this.root.querySelectorAll<HTMLButtonElement>("[data-remove]").forEach((button) => button.addEventListener("click", () => {
      this.selection.participants = this.selection.participants.filter((item) => item.slotId !== button.dataset.remove);
      this.normalizeColors();
      this.render();
    }));
    this.root.querySelector<HTMLButtonElement>("[data-randomize-grid]")?.addEventListener("click", () => this.randomizeGrid());
    this.root.querySelector<HTMLButtonElement>("[data-clear-grid]")?.addEventListener("click", () => {
      this.selection.participants = [];
      this.render();
    });
    this.root.querySelector<HTMLButtonElement>("[data-add-driver]")?.addEventListener("click", () => this.addDriver());
    this.root.querySelector<HTMLButtonElement>("[data-start-race]")?.addEventListener("click", () => this.startRace());
  }

  private bindPickerActions(): void {
    const backdrop = this.root.querySelector<HTMLElement>(".modal-backdrop");
    backdrop?.addEventListener("click", (event) => {
      if ((event.target as HTMLElement).hasAttribute("data-close-picker")) {
        this.picker = null;
        this.render();
      }
    });
    this.root.querySelectorAll<HTMLElement>("[data-close-picker]").forEach((element) => element.addEventListener("click", (event) => {
      if ((event.currentTarget as HTMLElement).classList.contains("modal-backdrop")) return;
      this.picker = null;
      this.render();
    }));
    this.root.querySelectorAll<HTMLButtonElement>("[data-pick-driver]").forEach((button) => button.addEventListener("click", () => {
      const participant = this.selection.participants.find((item) => item.slotId === this.picker?.slotId);
      if (participant) participant.driverId = button.dataset.pickDriver ?? participant.driverId;
      this.picker = null;
      this.render();
    }));
    this.root.querySelectorAll<HTMLButtonElement>("[data-pick-vehicle]").forEach((button) => button.addEventListener("click", () => {
      const participant = this.selection.participants.find((item) => item.slotId === this.picker?.slotId);
      if (participant) participant.vehicleId = button.dataset.pickVehicle as VehicleId;
      this.picker = null;
      this.render();
    }));
    const modal = this.root.querySelector<HTMLElement>(".picker-modal");
    modal?.focus();
  }

  private addDriver(): void {
    const usedSlots = new Set(this.selection.participants.map((item) => item.slotId));
    const index = Array.from({ length: this.data.race.maximum_participants }, (_, item) => item + 1).find((item) => !usedSlots.has(`slot-${item}`));
    if (!index) return;
    const usedDrivers = new Set(this.selection.participants.map((item) => item.driverId));
    const driver = this.data.drivers.find((item) => item.default_picker && !usedDrivers.has(item.id)) ?? this.data.drivers.find((item) => item.default_picker);
    if (!driver) return;
    this.selection.participants.push({
      slotId: `slot-${index}`,
      driverId: driver.id,
      vehicleId: "standard",
      color: this.data.race.colors[this.selection.participants.length] ?? [230, 230, 230],
    });
    this.normalizeColors();
    this.picker = { kind: "driver", slotId: `slot-${index}` };
    this.render();
  }

  private randomizeGrid(): void {
    const choices = this.data.drivers.filter((driver) => driver.default_picker);
    if (choices.length === 0) return;
    const count = 3 + Math.floor(Math.random() * (this.data.race.maximum_participants - 2));
    const vehicles = this.data.vehicles.map((vehicle) => vehicle.key);
    this.selection.participants = Array.from({ length: count }, (_, index) => ({
      slotId: `slot-${index + 1}`,
      driverId: index === 0 ? "human" : choices[Math.floor(Math.random() * choices.length)]!.id,
      vehicleId: vehicles[Math.floor(Math.random() * vehicles.length)] ?? "standard",
      color: this.data.race.colors[index] ?? [230, 230, 230],
    }));
    this.render();
  }

  private normalizeColors(): void {
    this.selection.participants.forEach((participant, index) => {
      participant.color = this.data.race.colors[index] ?? [230, 230, 230];
    });
  }

  private drawPreviews(): void {
    this.root.querySelectorAll<HTMLCanvasElement>("[data-track-preview]").forEach((canvas) => {
      const track = this.data.tracks.find((item) => item.key === canvas.dataset.trackPreview);
      if (track) drawTrackPreview(canvas, track);
    });
    this.root.querySelectorAll<HTMLCanvasElement>("[data-vehicle-preview]").forEach((canvas) => {
      const vehicle = this.data.vehicles.find((item) => item.key === canvas.dataset.vehiclePreview);
      const color = (canvas.dataset.color ?? "230,230,230").split(",").map(Number) as [number, number, number];
      if (vehicle) drawVehiclePreview(canvas, vehicle, color);
    });
  }

  private startRace(): void {
    if (this.selection.participants.length === 0) return;
    const humanCount = this.selection.participants.filter((item) => item.driverId === "human").length;
    if (humanCount > 1) throw new Error("Et løb understøtter højst én menneskelig kører.");
    this.engine = new QuickRaceEngine(this.data, structuredClone(this.selection));
    this.leavingRace = false;
    void this.audio.activate();
    window.history.pushState({}, "", "/play/race");
    this.renderRaceScreen();
  }

  private renderRaceScreen(): void {
    if (!this.engine) return;
    this.disposeCockpit();
    this.audio.beginRace(this.engine);
    this.focusedParticipantId = this.engine.participants.find((p) => p.driver === null)?.selection.slotId ?? null;
    const hasHuman = this.engine.participants.some((participant) => participant.driver === null);
    this.root.innerHTML = `
      <main id="main" class="race-screen" data-view="2d">
        <canvas id="race-canvas" aria-label="NeuroDrive-løb"></canvas>
        <div class="race-topbar"><div class="race-brand"><img class="brand-mark brand-icon" src="/icon-192.png" alt=""><div><strong>${escapeHtml(this.engine.track.data.name)}</strong><small>${this.selection.mode === "on" ? "SAMMENSTØD TIL" : "SAMMENSTØD FRA"} · ${this.selection.laps} OMGANG${this.selection.laps === 1 ? "" : "E"} · ${this.selection.rabbitCount} KANINER</small></div></div><div class="race-clock"><small data-race-phase>LØBSTID</small><strong data-race-time>0.00</strong></div><div class="race-controls"><button type="button" class="race-view-toggle" data-view-toggle aria-pressed="false" aria-label="Skift til 3D"><span data-view-2d>2D</span><span data-view-3d>3D</span></button>${this.audioButtonMarkup()}<button type="button" data-pause>${this.paused ? "Fortsæt" : "Pause"}</button><button type="button" data-exit-race>Forlad løbet</button></div></div>
        <div class="race-view-message" data-view-message role="status" hidden></div>
        <aside class="race-standings"><p class="step-label">STILLING</p><div data-live-order></div></aside>
        <aside class="race-telemetry"><div><p class="step-label" data-telemetry-label>FØRENDE KØRER</p><strong data-telemetry-driver>—</strong><small data-telemetry-profile>—</small><small data-telemetry-version>—</small></div><div class="telemetry-rays" data-telemetry-rays></div><div class="control-meter"><span>RAT</span><i><b data-steer-meter></b></i></div><div class="control-meter"><span>SPEEDER / BREMSE</span><i><b data-throttle-meter></b></i></div></aside>
        <div class="race-announcer is-countdown" data-race-announcer role="status" aria-live="polite"><small>GØR KLAR</small><strong>3</strong><span>Samme start. Samme ur.</span></div>
        ${hasHuman ? `<div class="human-help">WASD / PILETASTER · BREMS FØR BAKGEAR</div><div class="touch-controls" aria-label="Trykknapper"><div><button type="button" data-touch="left" aria-label="Drej til venstre">←</button><button type="button" data-touch="right" aria-label="Drej til højre">→</button></div><div><button type="button" data-touch="gas" aria-label="Giv gas">↑</button><button type="button" data-touch="brake" aria-label="Brems og bak">↓</button></div></div>` : ""}
      </main>`;
    const canvas = this.root.querySelector<HTMLCanvasElement>("#race-canvas");
    if (!canvas) throw new Error("Løbets tegneflade mangler.");
    this.renderer = new RaceCanvasRenderer(canvas, this.data.game_semantics.world_size);
    const bindImmediateControl = (
      button: HTMLButtonElement | null,
      action: () => void,
    ): void => {
      button?.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        action();
      });
      button?.addEventListener("click", (event) => {
        // Tastaturaktivering har detail 0. Mus/touch blev allerede håndteret
        // på pointerdown og må ikke toggle handlingen en ekstra gang.
        if (event.detail === 0) action();
      });
    };
    bindImmediateControl(this.root.querySelector<HTMLButtonElement>("[data-view-toggle]"), () => { void this.toggleRaceView(); });
    const pauseButton = this.root.querySelector<HTMLButtonElement>("[data-pause]");
    if (pauseButton) pauseButton.disabled = true;
    bindImmediateControl(pauseButton, () => {
      if (!this.raceStarted) return;
      this.paused = !this.paused;
      const button = this.root.querySelector<HTMLButtonElement>("[data-pause]");
      if (button) button.textContent = this.paused ? "Fortsæt" : "Pause";
    });
    bindImmediateControl(
      this.root.querySelector<HTMLButtonElement>("[data-exit-race]"),
      () => this.exitRace(),
    );
    bindImmediateControl(
      this.root.querySelector<HTMLButtonElement>("[data-audio-toggle]"),
      () => this.handleAudioButton(),
    );
    const chooseFollowedDriver = (event: Event): void => {
      const button = (event.target as Element | null)?.closest<HTMLButtonElement>("[data-follow-driver]");
      if (!button?.dataset.followDriver) return;
      this.focusedParticipantId = this.raceView === "2d" && this.focusedParticipantId === button.dataset.followDriver
        ? null
        : button.dataset.followDriver;
      this.updateRaceHud();
    };
    const liveOrder = this.root.querySelector("[data-live-order]");
    liveOrder?.addEventListener("pointerdown", chooseFollowedDriver);
    liveOrder?.addEventListener("click", (event) => {
      if (event instanceof MouseEvent && event.detail > 0) return;
      chooseFollowedDriver(event);
    });
    this.bindTouchControls();
    this.previousFrame = performance.now();
    this.countdownStartedAt = this.previousFrame;
    this.accumulator = 0;
    this.paused = false;
    this.raceStarted = false;
    this.raceFinishedAt = null;
    this.winnerAnnouncedAt = null;
    this.announcerKey = "";
    this.animationFrame = requestAnimationFrame((time) => this.frame(time));
    if (this.preferredView === "3d") void this.toggleRaceView();
  }

  private frame(time: number): void {
    if (!this.engine || !this.renderer) return;
    const countdownElapsed = time - this.countdownStartedAt;
    let frameDelta = Math.min(0.25, Math.max(0, (time - this.previousFrame) / 1000));
    if (!this.raceStarted && raceHasStarted(countdownElapsed)) {
      this.raceStarted = true;
      this.previousFrame = time;
      this.accumulator = 0;
      frameDelta = 0;
      const pauseButton = this.root.querySelector<HTMLButtonElement>("[data-pause]");
      if (pauseButton) pauseButton.disabled = false;
    }
    this.previousFrame = time;
    if (this.raceStarted && !this.paused && !this.engine.finished) {
      this.accumulator += frameDelta;
      let catchup = 0;
      while (this.accumulator >= this.data.game_semantics.fixed_dt && catchup < 8) {
        this.engine.step(this.humanControl());
        this.accumulator -= this.data.game_semantics.fixed_dt;
        catchup += 1;
      }
      if (catchup === 8) this.accumulator = Math.min(this.accumulator, this.data.game_semantics.fixed_dt);
    }
    if (this.raceView === "3d" && this.cockpit && this.focusedParticipantId) {
      try { this.cockpit.render(this.engine, this.focusedParticipantId, this.paused ? 0 : frameDelta); }
      catch { this.fallbackTo2d(); }
    } else this.renderer.render(this.engine, this.focusedParticipantId);
    this.audio.updateRace(this.engine, this.raceStarted && !this.paused && !this.engine.finished, time);
    this.updateRaceHud();
    const winner = this.engine.results.find((result) => result.status === "FINISHED");
    if (winner && this.winnerAnnouncedAt === null) this.winnerAnnouncedAt = time;
    this.updateRaceAnnouncer(time, countdownElapsed);
    if (this.engine.finished) {
      this.raceFinishedAt ??= time;
      if (time - this.raceFinishedAt > 1_650) {
        this.renderResults(this.engine.results);
        return;
      }
    }
    this.animationFrame = requestAnimationFrame((next) => this.frame(next));
  }

  private updateRaceAnnouncer(time: number, countdownElapsed: number): void {
    if (!this.engine) return;
    const announcer = this.root.querySelector<HTMLElement>("[data-race-announcer]");
    if (!announcer) return;
    const cue = countdownCue(countdownElapsed);
    if (cue !== null) {
      const key = `countdown:${cue}`;
      if (this.announcerKey === key) return;
      this.announcerKey = key;
      this.audio.countdown(cue);
      announcer.hidden = false;
      announcer.className = `race-announcer is-countdown cue-${cue.toLowerCase()}`;
      announcer.innerHTML = `<small>${cue === "GO" ? "START" : "GØR KLAR"}</small><strong>${cue === "GO" ? "KØR!" : cue}</strong><span>${cue === "GO" ? "Fuld fart." : "Samme start. Samme ur."}</span>`;
      return;
    }
    const shouldShowFinish = this.engine.finished || (this.winnerAnnouncedAt !== null && time - this.winnerAnnouncedAt < 1_650);
    if (shouldShowFinish) {
      const cueText = finishCue(this.engine.results);
      const key = `finish:${cueText.kicker}:${cueText.headline}:${cueText.detail}`;
      if (this.announcerKey === key) return;
      this.announcerKey = key;
      announcer.hidden = false;
      announcer.className = "race-announcer is-finish";
      announcer.innerHTML = `<small>${escapeHtml(cueText.kicker)}</small><strong>${escapeHtml(cueText.headline)}</strong><span>${escapeHtml(cueText.detail)}</span>`;
      return;
    }
    if (this.announcerKey !== "hidden") {
      this.announcerKey = "hidden";
      announcer.hidden = true;
    }
  }

  private updateRaceHud(): void {
    if (!this.engine) return;
    const rankedResults = this.engine.results;
    const results = this.raceStarted
      ? rankedResults
      : this.engine.participants.map((participant) => rankedResults.find((result) => result.participantId === participant.selection.slotId)!);
    const time = this.root.querySelector<HTMLElement>("[data-race-time]");
    if (time) time.textContent = this.engine.elapsedSeconds.toFixed(2);
    const phase = this.root.querySelector<HTMLElement>("[data-race-phase]");
    if (phase) phase.textContent = this.engine.podiumGraceRemaining === null
      ? "LØBSTID"
      : `PODIET ER FUNDET · ${Math.ceil(this.engine.podiumGraceRemaining)} S`;
    const order = this.root.querySelector<HTMLElement>("[data-live-order]");
    if (order) order.innerHTML = results.map((result, index) => {
      const focused = this.focusedParticipantId === result.participantId;
      const participant = this.engine?.participants.find((item) => item.selection.slotId === result.participantId);
      const focusDetail = focused && participant
        ? `<em>${escapeHtml(participant.driver?.subtitle ?? "Manuel kørsel")} · fart ${Math.abs(participant.car.speed).toFixed(0)}</em>`
        : "";
      return `<button type="button" class="live-driver ${focused ? "is-focused" : ""}" data-follow-driver="${escapeHtml(result.participantId)}" aria-pressed="${focused}" aria-label="${focused && this.raceView === "2d" ? "Vis hele banen" : `Følg ${escapeHtml(result.displayName)}`}"><span style="--race-color:${colorCss(result.color)}">${index + 1}</span><p><strong>${escapeHtml(result.displayName)}</strong><small>${escapeHtml(this.vehicle(result.vehicleId).name)} · ${result.status === "RACING" ? `${result.progressLaps.toFixed(2)} omgange` : result.status === "FINISHED" ? "I mål" : "Udgået"}</small>${focusDetail}</p></button>`;
    }).join("");
    const selectedResult = results.find((result) => result.participantId === this.focusedParticipantId) ?? results[0];
    const selected = this.engine.participants.find((participant) => participant.selection.slotId === selectedResult?.participantId);
    if (!selected) return;
    const label = this.root.querySelector<HTMLElement>("[data-telemetry-label]");
    const driver = this.root.querySelector<HTMLElement>("[data-telemetry-driver]");
    const profile = this.root.querySelector<HTMLElement>("[data-telemetry-profile]");
    const version = this.root.querySelector<HTMLElement>("[data-telemetry-version]");
    if (label) label.textContent = this.raceView === "3d" ? "FØRERHUS 3D · VALGT KØRER" : this.focusedParticipantId ? "VALGT KØRER · KLIK IGEN FOR OVERBLIK" : "FØRENDE KØRER";
    if (driver) driver.textContent = selected.driver?.name ?? "Dig";
    if (profile) profile.textContent = selected.driver?.subtitle ?? "Du styrer bilen manuelt";
    if (version) {
      const motionState = selected.car.stuntSegmentId?.includes("tornado") ? ` · TORNADO ${Math.min(360, selected.car.stuntProgress * 180 / Math.PI).toFixed(0)}°` : selected.car.airborne ? ` · I LUFTEN ${selected.car.z.toFixed(1)}` : selected.car.onWater ? " · AQUAPLANING" : selected.car.onBridge ? " · PÅ BROEN" : selected.car.speed < -0.5 ? " · BAKKER" : selected.recovery.stuck ? " · SIDDER FAST" : "";
      version.textContent = `${selected.driver ? `${selected.driver.parameter_count} parametre` : "direkte styring"} · fart ${Math.abs(selected.car.speed).toFixed(0)}${motionState}`;
    }
    const rays = this.root.querySelector<HTMLElement>("[data-telemetry-rays]");
    if (rays) {
      const hitLabels = { TRACK: "BANE", VEHICLE: "BIL", RABBIT: "KANIN", NONE: "FRIT" } as const;
      rays.innerHTML = selected.readings.map((reading) => `<span class="ray-${reading.hitType.toLowerCase()}"><b>${hitLabels[reading.hitType]}</b><small>${reading.normalized.toFixed(2)}</small></span>`).join("");
    }
    const steer = this.root.querySelector<HTMLElement>("[data-steer-meter]");
    const throttle = this.root.querySelector<HTMLElement>("[data-throttle-meter]");
    if (steer) { steer.style.width = `${Math.abs(selected.control.steering) * 50}%`; steer.style.left = selected.control.steering < 0 ? `${50 - Math.abs(selected.control.steering) * 50}%` : "50%"; }
    if (throttle) { throttle.style.width = `${Math.abs(selected.control.throttleBrake) * 50}%`; throttle.style.left = selected.control.throttleBrake < 0 ? `${50 - Math.abs(selected.control.throttleBrake) * 50}%` : "50%"; }
  }

  private renderResults(results: RaceResult[]): void {
    this.stopAnimation();
    this.disposeCockpit();
    this.audio.silenceRace();
    this.audio.setScene("menu");
    const winner = results.find((result) => result.status === "FINISHED");
    const resultHeadline = winner ? `${winner.displayName} vinder!` : "Løbet er slut.";
    const podium = results.slice(0, 3).map((result, index) => {
      const driver = result.driverId === "human" ? null : this.driver(result.driverId);
      const portrait = driver
        ? `<div class="driver-avatar has-portrait" style="background-image:url('${driverPortraitsUrl}');--portrait-x:${(driver.portrait_index % 4) * 100 / 3}%;--portrait-y:${Math.floor(driver.portrait_index / 4) * 100 / 3}%"></div>`
        : `<div class="driver-avatar has-human-helmet" aria-label="Dig" style="background-image:url('${humanDriverHelmetUrl}')"></div>`;
      return `<article class="podium-place place-${index + 1}" style="--race-color:${colorCss(result.color)}"><span>${index + 1}</span>${portrait}<h3>${escapeHtml(result.displayName)}</h3><p>${escapeHtml(this.vehicle(result.vehicleId).name)}</p><strong>${result.status === "FINISHED" ? formatTime(result.finishTime) : "Udgået"}</strong></article>`;
    }).join("");
    this.root.innerHTML = `<main id="main" class="results-page"><section class="results-hero section-shell"><p class="eyebrow"><span></span> LØBET ER SLUT</p><h1>${escapeHtml(resultHeadline)}</h1><p>${escapeHtml(this.track().name)} · ${this.selection.laps} omgang${this.selection.laps === 1 ? "" : "e"} · ${this.selection.rabbitCount} kaniner</p></section><section class="podium section-shell">${podium}</section><section class="results-table-wrap section-shell"><div class="results-actions"><div><p class="step-label">RESULTATER</p><h2>Det skete på banen</h2></div><div><button class="button button-quiet" type="button" data-home>Forside</button><button class="button button-quiet" type="button" data-change-race>Skift løb</button><button class="button button-accent" type="button" data-rematch>Kør igen →</button></div></div><div class="results-scroll"><table><thead><tr><th>Plads</th><th>Kører</th><th>Bil</th><th>Resultat</th><th>Hurtigste omgang</th><th>Bande</th><th>Biler</th><th>Kaniner</th><th>Hop</th><th>Tornado</th></tr></thead><tbody>${results.map((result, index) => `<tr><td><span class="result-position" style="--race-color:${colorCss(result.color)}">${index + 1}</span></td><td><strong>${escapeHtml(result.displayName)}</strong></td><td>${escapeHtml(this.vehicle(result.vehicleId).name)}</td><td>${result.status === "FINISHED" ? formatTime(result.finishTime) : `Udgået · ${result.progressLaps.toFixed(2)} omgange`}</td><td>${formatTime(result.fastestLap)}</td><td>${result.trackCollisions}</td><td>${result.opponentCollisions}</td><td>${result.rabbitIncidents}</td><td>${result.cleanLandings}/${result.rampEntries}</td><td>${result.tornadoCompletions}/${result.tornadoEntries}</td></tr>`).join("")}</tbody></table></div></section></main>${this.footer()}`;
    this.root.querySelector("[data-rematch]")?.addEventListener("click", () => {
      this.engine = new QuickRaceEngine(this.data, exactRematchSelection(this.selection));
      window.history.pushState({}, "", "/play/race");
      this.renderRaceScreen();
    });
    this.root.querySelector("[data-change-race]")?.addEventListener("click", () => this.exitRace());
    this.root.querySelector("[data-home]")?.addEventListener("click", () => this.navigate("home"));
  }

  private bindTouchControls(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-touch]").forEach((button) => {
      const key = `touch:${button.dataset.touch}`;
      const start = (event: Event) => { event.preventDefault(); this.keys.add(key); };
      const end = (event: Event) => { event.preventDefault(); this.keys.delete(key); };
      button.addEventListener("pointerdown", start);
      button.addEventListener("pointerup", end);
      button.addEventListener("pointercancel", end);
      button.addEventListener("pointerleave", end);
    });
  }

  private handleKey(event: KeyboardEvent, down: boolean): void {
    if (this.picker && event.key === "Escape" && down) {
      this.picker = null;
      this.render();
      return;
    }
    if (!this.engine) return;
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(event.key)) event.preventDefault();
    if (event.key === "Escape" && down) { this.exitRace(); return; }
    if (event.key === " " && down && !event.repeat && this.raceStarted) {
      this.paused = !this.paused;
      return;
    }
    const key = event.key.toLowerCase();
    if (down) this.keys.add(key); else this.keys.delete(key);
  }

  private humanControl(): Control {
    const left = this.keys.has("a") || this.keys.has("arrowleft") || this.keys.has("touch:left");
    const right = this.keys.has("d") || this.keys.has("arrowright") || this.keys.has("touch:right");
    const gas = this.keys.has("w") || this.keys.has("arrowup") || this.keys.has("touch:gas");
    const brake = this.keys.has("s") || this.keys.has("arrowdown") || this.keys.has("touch:brake");
    return { steering: (right ? 1 : 0) - (left ? 1 : 0), throttleBrake: (gas ? 1 : 0) - (brake ? 1 : 0) };
  }

  private exitRace(): void {
    if (this.leavingRace) return;
    this.leavingRace = true;
    this.stopRace();
    this.route = "play";
    window.history.pushState({}, "", "/play");
    this.render();
  }

  private stopAnimation(): void {
    if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  private stopRace(): void {
    this.driveRequest++;
    this.endless?.dispose(); this.endless = null;
    this.stopAnimation();
    this.disposeCockpit();
    this.engine = null;
    this.renderer = null;
    this.keys.clear();
    this.paused = false;
    this.raceStarted = false;
    this.winnerAnnouncedAt = null;
    this.focusedParticipantId = null;
    this.audio.silenceRace();
    this.audio.setScene("menu");
  }

  private disposeCockpit(): void {
    this.viewAnimation?.cancel(); this.viewAnimation = null;
    this.viewRequest += 1;
    this.cockpit?.dispose();
    this.cockpit = null;
    this.loading3d = false;
    this.raceView = "2d";
  }

  private updateViewControls(message = ""): void {
    const screen = this.root.querySelector<HTMLElement>(".race-screen");
    const previousView = screen?.dataset.view;
    if (screen) screen.dataset.view = this.raceView;
    const canvas = this.root.querySelector<HTMLCanvasElement>("#race-canvas");
    if (canvas) canvas.hidden = this.raceView === "3d";
    if (previousView === "3d" && this.raceView === "2d" && canvas) {
      if (this.engine) this.renderer?.render(this.engine, this.focusedParticipantId);
      this.viewAnimation?.cancel();
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches)
        this.viewAnimation = canvas.animate([{transform:"scale(1.35)", opacity:.35}, {transform:"scale(1)", opacity:1}], {duration:450, easing:"cubic-bezier(.2,.7,.2,1)"});
    }
    this.cockpit?.setActive(this.raceView === "3d");
    this.audio.setScene(this.raceView === "3d" ? "race3d" : "race");
    const button = this.root.querySelector<HTMLButtonElement>("[data-view-toggle]");
    button?.setAttribute("aria-pressed", String(this.raceView === "3d"));
    button?.setAttribute("aria-label", this.loading3d ? "Annullér åbning af 3D" : this.raceView === "3d" ? "Skift til 2D" : "Skift til 3D");
    button?.setAttribute("aria-busy", String(this.loading3d));
    const status = this.root.querySelector<HTMLElement>("[data-view-message]");
    if (status) { status.textContent = message; status.hidden = !message; }
    this.updateRaceHud();
  }

  private fallbackTo2d(): void {
    this.preferredView = "2d"; saveViewPreference("2d");
    this.disposeCockpit();
    this.updateViewControls("3D kunne ikke vises. Løbet fortsætter i 2D.");
  }

  private async toggleRaceView(): Promise<void> {
    const engine = this.engine;
    if (!engine) return;
    if (this.raceView === "3d" || this.loading3d) {
      this.viewRequest += 1; this.loading3d = false; this.raceView = "2d";
      this.preferredView = "2d"; saveViewPreference("2d");
      this.updateViewControls(); return;
    }
    // Capture the leader ONCE, before import; later overtakes never change the camera.
    this.focusedParticipantId ??= engine.results[0]?.participantId ?? engine.participants[0]!.selection.slotId;
    if (this.cockpit) { this.preferredView = "3d"; saveViewPreference("3d"); this.raceView = "3d"; this.updateViewControls(); return; }
    const request = ++this.viewRequest;
    this.loading3d = true;
    this.updateViewControls("Åbner førerhuset …");
    let canvas: HTMLCanvasElement | null = null;
    try {
      const { CockpitRenderer } = await import("../render/cockpit/renderer");
      if (this.engine !== engine || request !== this.viewRequest) return;
      canvas = document.createElement("canvas");
      canvas.id = "race-canvas-3d"; canvas.setAttribute("aria-label", "NeuroDrive · 3D");
      this.root.querySelector(".race-screen")!.prepend(canvas);
      this.cockpit = CockpitRenderer.create(canvas, engine, () => this.fallbackTo2d());
      this.raceView = "3d"; this.loading3d = false;
      this.preferredView = "3d"; saveViewPreference("3d");
      this.updateViewControls();
    } catch {
      canvas?.remove();
      if (this.engine === engine && request === this.viewRequest) this.fallbackTo2d();
    }
  }

  private track() {
    const track = this.data.tracks.find((item) => item.key === this.selection.trackKey);
    if (!track) throw new Error(`Track ${this.selection.trackKey} is missing.`);
    return track;
  }

  private driver(id: string): DriverData {
    const driver = this.data.drivers.find((item) => item.id === id);
    if (!driver) throw new Error(`Driver ${id} is missing.`);
    return driver;
  }

  private vehicle(id: VehicleId): VehicleData {
    const vehicle = this.data.vehicles.find((item) => item.key === id);
    if (!vehicle) throw new Error(`Vehicle ${id} is missing.`);
    return vehicle;
  }
}
